import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaExternalLinkAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { fetchDrillDownUnits } from './propertyApi';

function formatCount(value) {
  return Number(value || 0).toLocaleString('en-IN');
}

function PropertyUnitDrillDown({
  drillDown,
  propertyType = 'All',
  propertyId = null,
  propertyName = null,
  onBack,
  onOpenUnit,
}) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!drillDown) {
      return undefined;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    fetchDrillDownUnits({
      drillDown,
      propertyType,
      propertyId,
      propertyName,
      signal: controller.signal,
    })
      .then(result => {
        if (!controller.signal.aborted) {
          setData(result);
        }
      })
      .catch(err => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') {
          return;
        }
        console.error('Drill-down load failed:', err);
        setError(err?.message || 'Failed to load units');
        setData(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [drillDown, propertyType, propertyId, propertyName]);

  if (!drillDown) {
    return null;
  }

  const units = data?.units || [];

  return (
    <div className='re-drilldown'>
      <header className='re-drilldown__header'>
        <Button variant='link' className='re-drilldown__back' onClick={onBack}>
          <FaArrowLeft aria-hidden='true' /> Back to overview
        </Button>
        <div className='re-drilldown__titles'>
          <h2 className='re-drilldown__title'>{data?.title || 'Units'}</h2>
          {data?.subtitle && <p className='re-drilldown__subtitle'>{data.subtitle}</p>}
        </div>
        <Button
          variant='outline-secondary'
          size='sm'
          className='re-drilldown__list-link'
          onClick={() => navigate('/listview/unit/')}
        >
          <FaExternalLinkAlt className='me-1' aria-hidden='true' />
          Open unit list
        </Button>
      </header>

      {loading && !data ? (
        <div className='re-drilldown__loading'>
          <Spinner animation='border' size='sm' role='status' />
          <span>Loading units…</span>
        </div>
      ) : error && !data ? (
        <Alert variant='danger'>{error}</Alert>
      ) : units.length === 0 ? (
        <p className='re-drilldown__empty'>No units match this filter.</p>
      ) : (
        <div className='re-drilldown__table-wrap'>
          <table className='re-drilldown__table'>
            <thead>
              <tr>
                {!propertyId && <th>Property</th>}
                <th>Unit</th>
                <th>Type</th>
                <th>Rent</th>
                <th>Status</th>
                <th>Leads</th>
                <th aria-label='Actions' />
              </tr>
            </thead>
            <tbody>
              {units.map(unit => (
                <tr key={unit.id}>
                  {!propertyId && <td>{unit.propertyName}</td>}
                  <td>{unit.unitNumber || unit.name}</td>
                  <td>{unit.unitType || '—'}</td>
                  <td>{unit.rentDisplay}</td>
                  <td>
                    <span className={`re-drilldown__status re-drilldown__status--${unit.status?.toLowerCase()}`}>
                      {unit.status}
                    </span>
                  </td>
                  <td>{formatCount(unit.openInterestCount)}</td>
                  <td className='re-drilldown__actions'>
                    {onOpenUnit && (
                      <Button
                        variant='link'
                        size='sm'
                        className='p-0'
                        onClick={() =>
                          onOpenUnit({
                            propertyId: unit.propertyId,
                            propertyName: unit.propertyName,
                            unitId: unit.id,
                          })
                        }
                      >
                        View on floors
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PropertyUnitDrillDown;
